import React, { useEffect, useState } from 'react';
import { Box, Select, MenuItem, Table, TableBody, TableCell, TableRow, Typography } from '@mui/material';
import { useInput, required } from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';
import { CarPlateField } from './CarPlateField';

const plateStyles = {
  private: { backgroundColor: '#ededed', borderColor: '#666' },
  commercial: { backgroundColor: '#007ac2', borderColor: '#004a70' },
  transport: { backgroundColor: '#ffcc00', borderColor: '#333' },
};

const arabicLetters = ['ا', 'ب', 'ح', 'د', 'ر', 'س', 'ص', 'ط', 'ع', 'ق', 'ك', 'ل', 'م', 'ن', 'ه', 'و', 'ي'];
const englishLetters = ['A', 'B', 'H', 'D', 'R', 'S', 'T', 'G', 'K', 'L', 'M', 'N', 'H', 'W', 'Y'];

// Import the useWatchMainChanged hook from vehicle.jsx
import { useWatchMainChanged } from '../OdsSearch/vehicle';

// Ensure plate format is always "number letter letter letter"
const parseValue = (value) => {
  if (!value) return ""; 
  const parts = value.trim().split(/\s+/);
  if (parts.length < 4) {
    const number = parts[0] || "";
    const letters = parts.slice(1).concat(Array(3 - parts.slice(1).length).fill("")); // Ensure 3 letters
    return `${number} ${letters.join(" ")}`;
  }
  return value;
};

export const CarPlateInputWatch = (props) => {
  console.log('🔥 CarPlateInputWatch rendered with props:', props);
  
  const { source, is_required, watched_column, type = 'transport', showEnglish = false } = props;
  
  // Validate required props
  if (!source || typeof source !== 'string') {
    console.error('CarPlateInputWatch: source is required and must be a string', { source, props });
    return <div>Error: Missing or invalid source prop</div>;
  }
  
  if (!watched_column || typeof watched_column !== 'string') {
    console.error('CarPlateInputWatch: watched_column is required and must be a string', { watched_column, props });
    return <div>Error: Missing or invalid watched_column prop</div>;
  }
  
  // Get form context and watch functionality  
  const { getValues } = useFormContext();
  const { disabled } = useWatchMainChanged(watched_column);
  
  // Get the field using useInput hook
  const { field } = useInput({ 
    source: source,
    validate: is_required ? [required()] : undefined 
  });

  console.log('🔥 Current field value:', field.value, 'disabled:', disabled);

  // Parse the current value to extract number and letters
  const parseCurrentValue = (value) => {
    if (!value || typeof value !== 'string') return { number: '', letters: ['', '', ''] };

    const parts = value.split(' ');
    const number = parts[0] || '';
    const storedLetters = parts.slice(1);
    
    // Handle "1234 ABC" format (letters combined)
    if (storedLetters.length === 1 && storedLetters[0].length > 1) {
      const letterString = storedLetters[0];
      const letters = letterString.split('').slice(0, 3);
      while (letters.length < 3) letters.push('');
      return { number, letters };
    }

    const letters = storedLetters.slice(0, 3);
    while (letters.length < 3) letters.push('');
    return { number, letters };
  };

  const { number, letters } = parseCurrentValue(field.value);
  
  // Update field value when user changes input
  const updateField = (newNumber, newLetters) => {
    const newValue = `${newNumber} ${newLetters.join(' ')}`;
    console.log('🔥 Updating field to:', newValue);
    field.onChange(newValue);
  };

  const handleNumberChange = (e) => {
    if (disabled) return;
    const newNumber = e.target.value.replace(/[^0-9]/g, '');
    updateField(newNumber, letters);
  };

  const handleLetterChange = (index) => (e) => {
    if (disabled) return;
    const newLetters = [...letters];
    newLetters[index] = e.target.value;
    updateField(number, newLetters);
  };

  const plateStyle = plateStyles[type] || plateStyles.transport;

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        padding: 0,
        width: '100%',
        minWidth: '210px',
        margin: 'auto',
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.2)',
        backgroundColor: disabled ? '#f5f5f5' : '#fff',
        direction: 'rtl',
        border: `2px solid ${plateStyle.borderColor}`,
        opacity: disabled ? 0.7 : 1,
      }}
    >
      <Table sx={{ width: '100%', borderCollapse: 'collapse', textAlign: 'center' }}>
        <TableBody>
          <TableRow>
            <TableCell
              rowSpan={showEnglish ? 2 : 1}
              sx={{
                width: '70px',
                backgroundImage: 'url(./ksa-logo.png)',
                backgroundSize: 'contain',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                border: `1px solid ${plateStyle.borderColor}`,
                backgroundColor: plateStyle.backgroundColor,
              }}
            />
            {letters.map((letter, index) => (
              <TableCell
                key={`arabic-${index}`}
                sx={{
                  padding: '1px',
                  border: `1px solid ${plateStyle.borderColor}`,
                  backgroundColor: disabled ? '#f5f5f5' : '#fff',
                }}
              >
                <Select
                  value={letter || ""}
                  onChange={handleLetterChange(index)}
                  disabled={disabled}
                  fullWidth
                  size="small"
                  sx={{
                    fontSize: { xs: '14px', md: '14px' },
                    textAlign: 'center',
                    fontFamily: 'Arial, sans-serif',
                  }}
                >
                  <MenuItem value="">-</MenuItem>
                  {arabicLetters.map((arLetter) => (
                    <MenuItem key={arLetter} value={arLetter}>
                      {arLetter}
                    </MenuItem>
                  ))}
                </Select>
              </TableCell>
            ))}
            <TableCell
              sx={{
                padding: 0,
                border: `1px solid ${plateStyle.borderColor}`,
                backgroundColor: disabled ? '#f5f5f5' : '#fff',
                width: '100%',
                height: '100%',
              }}
            >
              <input
                type="text"
                value={number}
                onChange={handleNumberChange}
                disabled={disabled}
                maxLength={4}
                placeholder="رقم"
                style={{
                  width: '100%',
                  height: '100%',
                  textAlign: 'center',
                  fontSize: '14px',
                  fontFamily: 'Arial, sans-serif',
                  padding: '8px 4px',
                  border: 'none',
                  outline: 'none',
                  backgroundColor: 'transparent',
                }}
              />
            </TableCell>
          </TableRow>

          {showEnglish && (
            <TableRow>
              {letters.map((letter, index) => (
                <TableCell
                  key={`english-${index}`}
                  sx={{
                    padding: '4px',
                    border: `1px solid ${plateStyle.borderColor}`,
                    backgroundColor: disabled ? '#f5f5f5' : '#fff',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: { xs: '12px', md: '12px' },
                      fontWeight: 'bold',
                      fontFamily: 'Arial, sans-serif',
                    }}
                  >
                    {englishLetters[arabicLetters.indexOf(letter)] || ""}
                  </Typography>
                </TableCell>
              ))}
              <TableCell
                sx={{
                  padding: '4px',
                  border: `1px solid ${plateStyle.borderColor}`,
                  backgroundColor: disabled ? '#f5f5f5' : '#fff',
                }}
              >
                <Typography
                  sx={{
                    fontSize: { xs: '12px', md: '12px' },
                    fontWeight: 'bold',
                    textAlign: 'center',
                    fontFamily: 'Arial, sans-serif',
                  }}
                >
                  {number}
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Box>
  );
};

export const CarPlateInput = ({ source, disabled = false, type = 'transport', showEnglish = false }) => {
  const { field } = useInput({ source });

  const [plateNumber, setPlateNumber] = useState(() => parseValue(field.value));

  useEffect(() => {
    if (!plateNumber) {
      setPlateNumber(parseValue(field.value));
    }
  }, []); 

  const updateFieldValue = (newPlate) => {
    setPlateNumber(newPlate);
    field.onChange(newPlate);
  };

  const handleNumberChange = (e) => {
    const newNumber = e.target.value.replace(/[^0-9]/g, '');
    const [, ...letters] = plateNumber.split(' ');
    updateFieldValue(`${newNumber} ${letters.join(' ')}`);
  };

  const handleLetterChange = (index) => (e) => {
    const parts = plateNumber.split(' ');
    const number = parts[0] || "";
    const letters = parts.slice(1).concat(Array(3 - parts.slice(1).length).fill("")); // Ensure 3 letters
    letters[index] = e.target.value; // Update letter
    updateFieldValue(`${number} ${letters.join(' ')}`);
  };

  const parts = plateNumber.split(' ').concat(["", "", "", ""]).slice(0, 4);
  const number = parts[0] || "";
  const letters = parts.slice(1, 4);

  const plateStyle = plateStyles[type] || plateStyles.transport;

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        padding: 0,
        width: '100%',
        minWidth: '210px',
        margin: 'auto',
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.2)',
        backgroundColor: '#fff',
        direction: 'rtl',
        border: `2px solid ${plateStyle.borderColor}`,
      }}
    >
      <Table sx={{ width: '100%', borderCollapse: 'collapse', textAlign: 'center' }}>
        <TableBody>
          <TableRow>
            <TableCell
              rowSpan={2}
              sx={{
                width: '70px',
                backgroundImage: 'url(./ksa-logo.png)',
                backgroundSize: 'contain',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                border: `1px solid ${plateStyle.borderColor}`,
                backgroundColor: plateStyle.backgroundColor,
              }}
            />
            {letters.map((letter, index) => (
              <TableCell
                key={`arabic-${index}`}
                sx={{
                  padding: '1px',
                  border: `1px solid ${plateStyle.borderColor}`,
                  backgroundColor: '#fff',
                }}
              >
                <Select
                  value={letter || ""}
                  onChange={handleLetterChange(index)}
                  disabled={disabled}
                  fullWidth
                  sx={{
                    fontSize: { xs: '14px', md: '14px' },
                    textAlign: 'center',
                    fontFamily: 'Arial, sans-serif',
                  }}
                >
                  {arabicLetters.map((arLetter) => (
                    <MenuItem key={arLetter} value={arLetter}>
                      {arLetter}
                    </MenuItem>
                  ))}
                </Select>
              </TableCell>
            ))}
            <TableCell
              sx={{
                padding: 0,
                border: `1px solid ${plateStyle.borderColor}`,
                backgroundColor: '#fff',
                width: '100%',
                height: '100%',
              }}
            >
              <input
                type="text"
                value={number}
                onChange={handleNumberChange}
                disabled={disabled}
                maxLength={4}
                style={{
                  width: '100%',
                  height: '100%',
                  textAlign: 'center',
                  fontSize: '14px',
                  fontFamily: 'Arial, sans-serif',
                  padding: 0,
                  border: `1px solid #ddd`,
                  outline: 'none',
                  backgroundColor: '#fff',
                }}
              />
            </TableCell>
          </TableRow>

          {showEnglish && (
            <TableRow>
              {letters.map((letter, index) => (
                <TableCell
                  key={`english-${index}`}
                  sx={{
                    padding: '4px',
                    border: `1px solid ${plateStyle.borderColor}`,
                    backgroundColor: '#fff',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: { xs: '14px', md: '14px' },
                      fontWeight: 'bold',
                      fontFamily: 'Arial, sans-serif',
                    }}
                  >
                    {englishLetters[arabicLetters.indexOf(letter)] || ""}
                  </Typography>
                </TableCell>
              ))}
              <TableCell
                sx={{
                  padding: '4px',
                  border: `1px solid ${plateStyle.borderColor}`,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  sx={{
                    fontSize: { xs: '14px', md: '14px' },
                    fontWeight: 'bold',
                    textAlign: 'center',
                    fontFamily: 'Arial, sans-serif',
                  }}
                >
                  {number}
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Box>
  );
};

export const CarPlateFilter = ({ source, type = 'transport', showEnglish = false }) => {
  const { field } = useInput({ source });

  const [plateNumber, setPlateNumber] = useState(() => parseValue(field.value));

  useEffect(() => {
    field.onChange(plateNumber);
  }, [plateNumber]);

  const updateFieldValue = (newPlate) => {
    setPlateNumber(newPlate);
    field.onChange(newPlate);
  };

  const handleNumberChange = (e) => {
    const newNumber = e.target.value.replace(/[^0-9]/g, '');
    const [, ...letters] = plateNumber.split(' ');
    updateFieldValue(`${newNumber} ${letters.join(' ')}`);
  };

  const handleLetterChange = (index) => (e) => {
    const parts = plateNumber.split(' ');
    const number = parts[0] || "";
    const letters = parts.slice(1).concat(Array(3 - parts.slice(1).length).fill(""));
    letters[index] = e.target.value;
    updateFieldValue(`${number} ${letters.join(' ')}`);
  };

  const parts = plateNumber.split(' ').concat(["", "", "", ""]).slice(0, 4);
  const number = parts[0] || "";
  const letters = parts.slice(1, 4);

  const plateStyle = plateStyles[type] || plateStyles.transport;

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        padding: 0,
        width: '230px',
        height: '50px',
        minWidth: '110px',
        margin: 'auto',
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.2)',
        backgroundColor: '#fff',
        direction: 'rtl',
        border: `2px solid ${plateStyle.borderColor}`,
      }}
    >
      <Table sx={{ width: '100%', borderCollapse: 'collapse', textAlign: 'center' }}>
        <TableBody>
          <TableRow>
            <TableCell
              rowSpan={2}
              sx={{
                width: '70px',
                backgroundImage: 'url(./ksa-logo.png)',
                backgroundSize: 'contain',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                border: `1px solid ${plateStyle.borderColor}`,
                backgroundColor: plateStyle.backgroundColor,
              }}
            />
            {letters.map((letter, index) => (
              <TableCell
                key={`arabic-${index}`}
                sx={{
                  padding: '1px',
                  border: `1px solid ${plateStyle.borderColor}`,
                  backgroundColor: '#fff',
                }}
              >
                <Select
                  value={letter || ""}
                  onChange={handleLetterChange(index)}
                  fullWidth
                  sx={{
                    fontSize: { xs: '14px', md: '14px' },
                    textAlign: 'center',
                    fontFamily: 'Arial, sans-serif',
                  }}
                >
                  {arabicLetters.map((arLetter) => (
                    <MenuItem key={arLetter} value={arLetter}>
                      {arLetter}
                    </MenuItem>
                  ))}
                </Select>
              </TableCell>
            ))}
            <TableCell
              sx={{
                padding: 0,
                border: `1px solid ${plateStyle.borderColor}`,
                backgroundColor: '#fff',
                width: '100%',
                height: '100%',
              }}
            >
              <input
                type="text"
                value={number}
                onChange={handleNumberChange}
                maxLength={4}
                style={{
                  width: '100%',
                  height: '100%',
                  textAlign: 'center',
                  fontSize: '14px',
                  fontFamily: 'Arial, sans-serif',
                  padding: 0,
                  border: `1px solid #ddd`,
                  outline: 'none',
                  backgroundColor: '#fff',
                }}
              />
            </TableCell>
          </TableRow>

          {showEnglish && (
            <TableRow>
              {letters.map((letter, index) => (
                <TableCell
                  key={`english-${index}`}
                  sx={{
                    padding: '4px',
                    border: `1px solid ${plateStyle.borderColor}`,
                    backgroundColor: '#fff',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: { xs: '14px', md: '14px' },
                      fontWeight: 'bold',
                      fontFamily: 'Arial, sans-serif',
                    }}
                  >
                    {englishLetters[arabicLetters.indexOf(letter)] || ""}
                  </Typography>
                </TableCell>
              ))}
              <TableCell
                sx={{
                  padding: '4px',
                  border: `1px solid ${plateStyle.borderColor}`,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  sx={{
                    fontSize: { xs: '14px', md: '14px' },
                    fontWeight: 'bold',
                    textAlign: 'center',
                    fontFamily: 'Arial, sans-serif',
                  }}
                >
                  {number}
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Box>
  );
};